import * as React from 'react';
import Control from 'react-leaflet-control';

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import {
  Paper,
  Typography,
  Button,
  Snackbar,
  CircularProgress
} from '@material-ui/core';

import {
  FarmMap,
  SelectLayerDialog,
  EditBinsDialog,
  FieldClickEventArgs,
} from '../components';

import { timestampToString, GeotiffRender } from '../utils';

import {
  LayoutTools,
  LayoutToolsProvider,
  LayersProvider,
  CreateReportProvider,
} from '../providers';

import DownloadReportDialog from '../components/DownloadReportDialog';

import DownloadIcon from '@material-ui/icons/CloudDownload';
import LayerIcon from '@material-ui/icons/RemoveRedEye';

interface Props {
  farm: Farm;
  className?: string;
  onFieldClick: (args: FieldClickEventArgs) => void;
}

interface State {
  selectedField: Field | null;
  selectedLayer: RasterDocument | null;
  selectedRender: GeotiffRender | null;
  showSelectLayer: boolean;
  showBinsDialog: boolean;
  showReportDialog: boolean;
}

interface Computed {
  layout: LayoutTools;
  layersLoading: boolean;
  availableLayers: RasterDocument[] | null;
  createReport: () => void;
  reportLoading: boolean;
  reportDocument: ReportDocument;
}

function computeLayerPolling(field: Field | null): number | undefined {
  return field && field.status && !field.status.ready ? 10 * 1000 : undefined;
}

export default class RasterMap extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedField: null,
      selectedLayer: null,
      selectedRender: null,
      showSelectLayer: false,
      showBinsDialog: false,
      showReportDialog: false
    };
  }

  handleFieldClick = (args: FieldClickEventArgs) => {
    const { onFieldClick } = this.props;

    this.setState({
      selectedField: args.field,
      selectedLayer: args.layer || null,
      selectedRender: args.render || null
    });

    onFieldClick(args);
  };

  handleReferenceClick = (args: FieldClickEventArgs) => {
    this.setState({
      selectedField: args.field,
      selectedLayer: args.layer || null,
      selectedRender: args.render || null,
      showBinsDialog: true
    });
  }

  async handleLayerSelected(layout: LayoutTools, document: RasterDocument) {
    const { selectedField } = this.state;
    
    await this.setState({ showSelectLayer: false });

    if (!selectedField) {
      return;
    }

    layout.pinLayer(selectedField.id, document);
  }

  async handleCustomBins(layout: LayoutTools, customBins: NdviRasterBins) {
    const { selectedField } = this.state;

    if (!selectedField) {
      return;
    }

    await this.setState({ showBinsDialog: false });

    layout.changeLayerBins(selectedField.id, customBins);
  }

  handleCreateReport = (computed: Computed) => {
    computed.createReport();
    this.setState({ showReportDialog: true });
  };

  renderLoading(message?: string | null) {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={true}
        message={
          <>
            <CircularProgress color="secondary" size={25} />
            <span>{message || 'cargando'}</span>
          </>
        }
      />
    );
  }

  renderEmptyLayerButton() {
    return (
      <Button
        variant="fab"
        color="secondary"
        onClick={() => this.setState({ showSelectLayer: true })}
      >
        <LayerIcon />
      </Button>
    );
  }

  renderCurrentLayerButton(layer: RasterDocument) {
    return (
      <Paper
        style={{
          width: 80,
          height: 80,
          padding: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
        onClick={() => this.setState({ showSelectLayer: true })}
      >
        <Typography>{layer.kind}</Typography>
        <Typography>{timestampToString(layer.capturedOn)}</Typography>
        <Typography>{layer.sceneSource}</Typography>
      </Paper>
    );
  }

  renderCreateReportButton(computed: Computed) {
    return (
      <Button
        variant="extendedFab"
        color="secondary"
        onClick={() => this.handleCreateReport(computed)}
      >
        Descargar como reporte
        <DownloadIcon style={{ marginLeft: 16 }} />
      </Button>
    );
  }

  composeProviders(children: (computed: Computed) => React.ReactNode) {
    const { farm } = this.props;
    const { selectedField, selectedLayer } = this.state;

    return (
      <LayoutToolsProvider>
        {layout => (
          <CreateReportProvider
            type="raster"
            farmId={farm.id}
            params={
              selectedLayer && {
                documentId: selectedLayer.id,
                customBins: selectedLayer.bins
              }
            }
          >
            {reportResult => (
              <LayersProvider
                farmId={farm.id}
                fieldId={selectedField && selectedField.id}
                first={40}
                pollInterval={computeLayerPolling(selectedField)}
              >
                {layersResult =>
                  children({
                    layout,
                    layersLoading: layersResult.loading,
                    availableLayers: layersResult.layers,
                    createReport: reportResult.create,
                    reportLoading: reportResult.loading,
                    reportDocument: reportResult.report,
                  })
                }
              </LayersProvider>
            )}
          </CreateReportProvider>
        )}
      </LayoutToolsProvider>
    );
  }

  render() {
    const { farm, className } = this.props;

    const {
      selectedField,
      selectedLayer,
      selectedRender,
      showBinsDialog,
      showReportDialog,
      showSelectLayer
    } = this.state;

    return this.composeProviders(computed => (
      <>
        {computed.layersLoading && this.renderLoading('buscando capas')}
        <FarmMap
          className={className}
          farm={farm}
          selectedId={selectedField && selectedField.id}
          onFieldClick={this.handleFieldClick}
          pinnedLayers={computed.layout.state.pinnedLayers}
          onReferenceClick={this.handleReferenceClick}
        >
          <Control position="bottomleft">
            <>
              {selectedLayer && this.renderCurrentLayerButton(selectedLayer)}
              {!selectedLayer && selectedField && this.renderEmptyLayerButton()}
            </>
          </Control>

          <Control position="bottomright">
            <>
              {selectedLayer &&
                selectedLayer.kind === 'raster-ndvi' &&
                this.renderCreateReportButton(computed)}
            </>
          </Control>

        </FarmMap>

        <SelectLayerDialog
          status={selectedField && selectedField.status}
          layers={computed.availableLayers}
          show={(computed.availableLayers && showSelectLayer) || false}
          onDismiss={() => this.setState({ showSelectLayer: false })}
          onSelect={l => this.handleLayerSelected(computed.layout, l)}
        />

        <EditBinsDialog
          bins={selectedRender && selectedRender.bins}
          histogram={selectedRender && selectedRender.histogram}
          totalArea={selectedField && selectedField.area}
          open={showBinsDialog}
          onClose={() => this.setState({ showBinsDialog: false })}
          onSubmit={bins => this.handleCustomBins(computed.layout, bins)}
        />

        <DownloadReportDialog
          open={showReportDialog}
          report={computed.reportDocument}
          onDismiss={() => this.setState({ showReportDialog: false })}
        />
      </>
    ));
  }
}



// WEBPACK FOOTER //
// ./src/containers/FarmExploreView.tsx